import React, { useCallback, useState } from 'react';
import { useTheme } from '../../../../theme';
import SocialAuth from '../../../../SocialAuth';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { emailRegex, MOBILE_MIN_WIDTH } from '../../../../utils';
import SubmitButton from '../SubmitButton/SubmitButton';
import LoginInput from '../LoginInput/LoginInput';
import { useConfig } from '../../../../context';
import { useMediaQuery } from 'react-responsive';
import { LoginType, useAccountExistenceQuery, useSendSecureCodeMutation } from '@websky/graphql';
import { clearPhoneNumber, isCorrectPhoneNumber } from '../../../../PhoneInput/utils';
import { useToggleable } from '../../../../hooks';
import { getCorporateNumber } from '../../../../Loyalty/utils';
import { Switch } from '@shared/ui';
import { detectInputType, isEmailDomainBlockedForRegistration } from '../../../utils';
import { LoginInputType } from '../../../types';
import { useLoginFormError } from '../../../hooks';

interface FormValues {
	login: string;
	email?: string;
}

export interface LoginFormValues extends FormValues {
	type: LoginType;
	needRegistration: boolean;
	code?: string;
}

export interface Props {
	title?: string;
	description?: React.ReactNode;
	initialData?: Partial<FormValues>;
	onSuccess: (data: LoginFormValues) => void;
	onError: (error: string) => void;
	stayLoggedIn?: boolean;
	setStayLoggedIn: (stayLogged: boolean) => void;
}

const LoginForm: React.FC<Props> = ({
	title,
	description,
	onSuccess,
	initialData,
	stayLoggedIn,
	setStayLoggedIn,
	onError
}) => {
	const { LoginForm: css } = useTheme('LoginPage');
	const { t } = useTranslation('LoginPage');
	const {
		global: {
			socialAuthMethods,
			authMethods,
			companyInfo: { iataCode }
		}
	} = useConfig();

	const isB2 = iataCode === 'B2';

	const FFPRegex = useConfig().global.FFPRegex;
	const availableAuthMethods = socialAuthMethods.filter(method => method.isAvailable);

	const [isLoading, setLoading] = useState(false);
	const [isShownCorporateCardSwitch, setShownCorporateCardSwitch] = useState(false);
	const [sendCode] = useSendSecureCodeMutation();

	const { refetch: userExistence } = useAccountExistenceQuery({
		skip: true
	});

	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });

	const { isOpen: isActiveCorporateCard, toggle: onToggleCorporateCard, close: onHideCorporateCard } = useToggleable(
		false
	);

	const { setError, setDefaultError } = useLoginFormError(onError);

	const validate = useCallback((values: FormValues): object => {
		const errors = {};

		if (values.login) {
			if (!emailRegex.test(values.login) && !isCorrectPhoneNumber(values.login) && !FFPRegex.test(values.login)) {
				errors['login'] = t('Please enter a valid Email / Phone / Card number');
			}

			if (detectInputType(values.login) === LoginInputType.Email && values.login.includes('*')) {
				errors['login'] = t('Please enter a valid Email');
			}
		} else {
			errors['login'] = t('Required');
		}

		return errors;
	}, []);

	const onSubmit = useCallback(
		async (values: FormValues) => {
			setLoading(true);

			const pureLogin = values.login;
			let login = pureLogin;
			let loginType: LoginType;

			if (emailRegex.test(login)) {
				loginType = LoginType.Email;
			} else if (isCorrectPhoneNumber(login)) {
				loginType = LoginType.Phone;
				login = clearPhoneNumber(pureLogin);
			} else if (FFPRegex.test(login)) {
				loginType = LoginType.FFP;
				if (isActiveCorporateCard) {
					login = getCorporateNumber(pureLogin);
				}
			}

			if (loginType === LoginType.FFP) {
				setLoading(false);

				onSuccess({
					login: login,
					type: loginType,
					needRegistration: false
				});
			}

			if (loginType === LoginType.Email || loginType === LoginType.Phone) {
				try {
					const isAccountExistence = await userExistence({
						params: {
							login,
							loginType
						}
					});

					if (isAccountExistence?.data?.AccountExistence?.result) {
						const data = await sendCode({
							variables: {
								params: {
									login,
									loginType
								}
							}
						});

						if (data.data.SendSecureCode.result) {
							onSuccess({
								login,
								type: loginType,
								needRegistration: false
							});
						} else {
							setDefaultError();
						}
					} else {
						if (
							loginType === LoginType.Email &&
							isEmailDomainBlockedForRegistration(login, authMethods?.blockedRegistrationEmailDomains)
						) {
							onError(t('Registration with this email domain is forbidden'));

							return;
						}

						onSuccess({
							login,
							type: loginType,
							needRegistration: true
						});
					}
				} catch (e) {
					setError(e);
				} finally {
					setLoading(false);
				}
			}
		},
		[onSuccess, onError, isActiveCorporateCard, t]
	);

	const setBelaviaCardType = useCallback(
		(value: string) => {
			if (value && FFPRegex.test(value)) {
				setShownCorporateCardSwitch(false); // WEBSKY-6899
			} else {
				setShownCorporateCardSwitch(false);
				onHideCorporateCard();
			}
		},
		[onHideCorporateCard]
	);

	const switchElements = (
		<div className={css.switch_elements}>
			<Switch
				classes={{
					label: css.switch__label
				}}
				checked={stayLoggedIn}
				onChange={() => setStayLoggedIn(!stayLoggedIn)}
				label={t('Remember me')}
			/>

			{isShownCorporateCardSwitch && (
				<Switch
					classes={{
						label: css.switch__label
					}}
					checked={isActiveCorporateCard}
					label={t('Passenger:Corporate card')}
					onChange={onToggleCorporateCard}
				/>
			)}
		</div>
	);

	return (
		<div className={css.root}>
			<div className={css.title}>{title || t('Log in')}</div>
			<div className={css.text}>
				{description || t(`If you don't have an account, it will be created automatically`)}
			</div>

			<Form<FormValues>
				initialValues={{
					login: initialData?.login
				}}
				validate={validate}
				onSubmit={!isLoading ? onSubmit : () => null}
				render={({ handleSubmit }) => (
					<form onSubmit={handleSubmit}>
						<div className={css.row}>
							<div className={css.input_wrapper}>
								<Field
									name={'login'}
									render={props => {
										return (
											<LoginInput
												{...props}
												input={{
													...props.input,
													onChange: isB2
														? event => {
																props.input.onChange(event);
																setBelaviaCardType(event.target.value);
														  }
														: props.input.onChange
												}}
											/>
										);
									}}
								/>
							</div>

							{isMobile && switchElements}

							<div className={css.button_wrapper}>
								<SubmitButton loading={isLoading}>{t('Continue')}</SubmitButton>
							</div>
						</div>
					</form>
				)}
			/>

			{!isMobile && switchElements}

			{availableAuthMethods.length > 0 && (
				<div className={css.social_wrapper}>
					<div className={css.social_title}>{t('or with you social network')}</div>
					<SocialAuth />
				</div>
			)}
		</div>
	);
};

export default LoginForm;
